// 휴대폰 번호 로그인 화면(개선항목 2,3).
// 이름(위) + 휴대폰 번호(아래)를 입력받고 인증번호를 발송한 뒤 /login/otp 로 이동한다.
// 이름은 신규 가입 시 회원 이름으로 저장된다(기존 회원 재로그인 시에는 무시). 끝까지(약관 단계) 전달된다.
// 소셜 콜백(SocialCallbackPage)에서 socialLink 를 들고 진입하면 "소셜 계정에 연결할 휴대폰 인증" 모드로 동작한다.

import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { normalizePhoneKr } from "@oo/shared";
import { sendOtp } from "@/features/auth/authApi.ts";
import { ApiError } from "@/api/client.ts";
import { formatPhoneKrInput } from "@/features/auth/formatPhone.ts";
import { Button } from "@/shared/ui";

// 소셜 콜백이 전달하는 link 정보. 존재하면 휴대폰 인증 후 이 소셜 식별자를 회원에 연결한다.
interface SocialLinkState {
  provider: "kakao" | "naver" | "google";
  linkToken: string;
}

const PROVIDER_LABEL: Record<SocialLinkState["provider"], string> = {
  kakao: "카카오",
  naver: "네이버",
  google: "구글",
};

// 휴대폰 번호 로그인 화면 컴포넌트.
export default function PhoneLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const socialLink =
    ((location.state ?? null) as { socialLink?: SocialLinkState } | null)?.socialLink ?? null;

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (name.trim().length === 0) {
      setError("이름을 입력해 주세요.");
      return;
    }
    const normalized = normalizePhoneKr(phone);
    if (!normalized) {
      setError("휴대폰 번호 형식이 올바르지 않습니다.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await sendOtp(normalized);
      // OTP 입력 화면으로 이동. 이름/소셜 link/devCode 를 함께 전달한다.
      navigate("/login/otp", {
        state: {
          phone: normalized,
          displayName: name.trim(),
          expiresInSec: result.expiresInSec,
          devCode: result.devCode,
          socialLink,
        },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-screen-sm flex-col gap-8 px-6 py-10">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold">{socialLink ? "휴대폰 인증" : "핸드폰 번호 로그인"}</h1>
        <p className="text-base text-ink-600">
          {socialLink
            ? `${PROVIDER_LABEL[socialLink.provider]} 계정 확인이 끝났습니다. 주문/수령 연락을 위해 휴대폰 번호를 인증해 주세요.`
            : "이름과 휴대폰 번호를 입력하시면 인증번호를 보내드립니다."}
        </p>
      </header>

      <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
        {/* 이름(위) - 신규 가입 시 회원 이름으로 저장 */}
        <label htmlFor="name" className="text-base font-medium">
          이름
        </label>
        <input
          id="name"
          name="name"
          type="text"
          autoComplete="name"
          maxLength={40}
          placeholder="주문 시 사용할 이름"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-12 rounded-md border border-ink-300 px-3 text-base"
        />

        {/* 휴대폰 번호(아래) */}
        <label htmlFor="phone" className="text-base font-medium">
          휴대폰 번호
        </label>
        <input
          id="phone"
          name="phone"
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          placeholder="010-0000-0000"
          maxLength={13}
          value={phone}
          onChange={(e) => setPhone(formatPhoneKrInput(e.target.value))}
          onPaste={(e) => {
            e.preventDefault();
            const text = e.clipboardData.getData("text");
            setPhone(formatPhoneKrInput(text));
          }}
          aria-invalid={error !== null}
          aria-describedby={error ? "phone-error" : undefined}
          className="h-12 rounded-md border border-ink-300 px-3 text-base tracking-wider"
        />
        {error ? (
          <p id="phone-error" className="text-sm text-danger-700">
            {error}
          </p>
        ) : null}
        <Button
          type="submit"
          fullWidth
          className="mt-2"
          disabled={phone.length === 0 || name.trim().length === 0}
          loading={submitting}
        >
          {submitting ? "발송 중..." : "인증번호 받기"}
        </Button>
      </form>
    </main>
  );
}
